import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";

import {
    CreateUIAnalyticsEvent,
    useAnalyticsEvents
} from "@atlaskit/analytics-next";
import FeatureGates from "@atlassian/feature-gate-js-client";
import { tenantType, userType } from "@atlassiansox/analytics-web-client";

import {
    getUfoSubstepForPolling,
    UfoCustomTimingKey,
    ufoEventConfirmationPageStatusPolling,
    ufoEventGraphQLGetProvisioningStatus,
    ufoEventGraphQLSubscriptionSetup,
    ufoEventRedirectToProduct
} from "@/components/signup/utils";
import {
    useBxpWebStargateClient,
    ProductActivationStatusResponse
} from "@/libraries/bxp-web-stargate-client/src/index";
import {
    ProductKey,
    useBXPExpressClient,
    useCofsClient
} from "@/libraries/signup/bxpkit-core/src";
import {
    JourneyLocalStorage,
    useAnchorLocalStorage
} from "@/utils/local-storage-hook";
import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { useAnalyticsClient } from "../../../../analytics/wac-analytics/analytics-client";
import { getIdentityTeamType } from "../models/user-segmentation";

import {
    trackPerceivedProvisioningEvent,
    trackGetProvisioningStatusLatencyEvent
} from "./../utils/analytics";
import { useConfirmationPage } from "./use-confirmation-page";
import { ConfirmationPageType } from "./use-confirmation-page-type";

import {
    useConfirmationPageType,
    useFreeBundlesStatus,
    useJiraTemplateChoice,
    useSearchParameters
} from "./";

const { GRAPHQL_SUBSCRIPTION_ACK, GRAPHQL_WEBHOOK_PROVISIONING_LATENCY } =
    UfoCustomTimingKey;

export const DEFAULT_TIMEOUT = 10 * 60000;
export const DEFAULT_RETRY_INTERVAL = 5000;
export const DEFAULT_CREATE_CLOUD_STATUS: CreateCloudStatus = "pending";
export const GRAPHQL_EXPERIMENT_TIMEOUT = 45000; // GraphQL timeout to fallback to control after 45 seconds

export type CreateCloudStatus = "pending" | "success" | "timeout" | "error";

export enum ConfirmationPageOverlayVariants {
    BUNDLES_MODAL = "bundles_modal",
    VIDEO_OVERLAY = "video_overlay",
    CONTROL = "control",
    UNENROLLED = "unenrolled"
}

export type FeatureFlags = {
    readonly provisionSecondPartyApps?: boolean;
    readonly enableBundlesAfterSignup?: boolean;
    readonly enableConfirmationPageOverlay?: ConfirmationPageOverlayVariants;
    readonly enableCutSignUpStepsInHalf?: boolean;
};

export type CloudStatus = {
    readonly isReferredFromVerifyEmail: boolean;
    readonly createCloudStatus: CreateCloudStatus;
    readonly progress: number;
    readonly featureFlags: FeatureFlags;
    readonly deferRedirectView?: ConfirmationPageType;
    readonly setDeferRedirectView?: (
        deferRedirectView: ConfirmationPageType | undefined
    ) => void;
    readonly redirectUrl: string;
    readonly perceivedProvisioningStartTime?: number | undefined;
    readonly setPerceivedProvisioningStartTime: (
        startTimeSeconds: number | undefined
    ) => void;
};

export function useCloudStatus() {
    return useContext(CloudStatusContext);
}

const CloudStatusContext = createContext<CloudStatus>({
    isReferredFromVerifyEmail: false,
    createCloudStatus: DEFAULT_CREATE_CLOUD_STATUS,
    progress: 0,
    featureFlags: {
        provisionSecondPartyApps: false,
        enableBundlesAfterSignup: false
    },
    redirectUrl: "",
    setPerceivedProvisioningStartTime: () => undefined
});

type ProgressBarStatusProps = {
    readonly startTime: number;
    readonly pivotPercentage: number;
    readonly progressBarPivotPoint: number;
    readonly slowProgressIncrement: number;
    readonly slowProgress: number;
    readonly interval?: NodeJS.Timer;
};

function getProgressbarStatus({
    startTime,
    pivotPercentage,
    progressBarPivotPoint,
    slowProgressIncrement,
    slowProgress,
    interval
}: ProgressBarStatusProps) {
    const currentTime = Date.now();
    const elapsedTime = currentTime - startTime;

    if (elapsedTime >= DEFAULT_TIMEOUT) {
        // @ts-ignore
        clearInterval(interval);
        return 1;
    } else if (elapsedTime <= progressBarPivotPoint) {
        const rapidProgress =
            (pivotPercentage / progressBarPivotPoint) * elapsedTime;
        return rapidProgress;
    } else {
        slowProgress =
            slowProgress == 0
                ? pivotPercentage
                : slowProgress + slowProgressIncrement;
        return slowProgress;
    }
}

export type CloudStatusProviderProps = {
    readonly retryInterval?: number;
    readonly timeout?: number;
    readonly isProduction?: boolean;
    readonly featureFlags: FeatureFlags;
    readonly provisioningRequestId: string;
    readonly onDemandUrl: string;
    readonly cloudId: string;
    readonly platform?: string;
    readonly requestId: string;
};

type GetCloudStatusType = () => Promise<boolean>;

const { useSubscription, gql } = require("@apollo/client");
const PROVISIONING_STATUS_SUBSCRIPTION = gql`
    subscription ProvisioningStatus($orchestrationId: String!) {
        signup {
            signupProvisioningStatus(orchestrationId: $orchestrationId) {
                orchestrationId
                provisionComplete
            }
        }
    }
`;

export const CloudStatusProvider = ({
    retryInterval = DEFAULT_RETRY_INTERVAL,
    timeout = DEFAULT_TIMEOUT,
    isProduction = false,
    children,
    featureFlags,
    provisioningRequestId,
    onDemandUrl,
    cloudId: cloudIdProp,
    platform,
    requestId
}: PropsWithChildren<CloudStatusProviderProps>) => {
    const cofsClient = useCofsClient();
    const stargateClient = useBxpWebStargateClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const {
        templateId,
        referrer,
        products,
        siteRenameRequestId,
        dataRegion,
        admin,
        isFoundingAdmin,
        signupSource,
        provisionApp,
        appKey,
        orchestrationId
    } = useSearchParameters();
    const productKey = (
        products.length > 0 ? products[0] : undefined
    ) as ProductKey;
    const isProductJsw =
        products.length === 1 && products.includes("jira-software.ondemand");
    const isProductConfluence =
        products.length === 1 && products.includes("confluence.ondemand");
    const isFreeBundlesEnabled =
        featureFlags?.enableBundlesAfterSignup && isProductJsw;
    const is15secondProvisioningEnabled = isProductJsw || isProductConfluence;
    const isPageOverlayModalEnabled =
        useConfirmationPageOverlayModal(featureFlags);
    const { bundleProvisioningStatus, setBundleProvisioningStatus } =
        useFreeBundlesStatus();
    const [createCloudStatus, setCreateCloudStatus] =
        useState<CreateCloudStatus>(timeout > 0 ? "pending" : "timeout");
    const [cloudId, setCloudId] = useState<string>(cloudIdProp);
    const hasRequiredParameters = !!onDemandUrl && !!products.length;
    const userProfile = useLoggedInUser();
    const analyticsClient = useAnalyticsClient();

    const [progress, setProgress] = useState(0);
    const isReferredFromVerifyEmail = referrer === "verify_email";
    const {
        userSegmentationContext: {
            shouldShowUserSegmentationPage,
            selectedTeamType
        }
    } = useConfirmationPage();

    const [deferRedirectView, setDeferRedirectView] = useState<
        ConfirmationPageType | undefined
    >(undefined);
    const [redirectUrl, setRedirectUrl] = useState("");

    const currentScreen = useConfirmationPageType(
        createCloudStatus,
        deferRedirectView
    );
    const finalProvisioningScreens: ConfirmationPageType[] = useMemo(
        () => ["robots", "jira-animated-provisioning-screen", "redirect"],
        []
    );
    const persona = useRef("");

    const [perceivedProvisioningStartTime, setPerceivedProvisioningStartTime] =
        useState<number | undefined>(undefined);

    // Start time for graphql's get provisioning latency
    const graphqlGetProvisioningStartTimeRef = useRef<number>(
        Date.now() / 1000
    );
    // Start time for perceived provisioning based on time spent on loading screens (dwell time)
    const perceivedProvisioningStartTimeRef = useRef<number>();
    useEffect(() => {
        perceivedProvisioningStartTimeRef.current =
            perceivedProvisioningStartTime;
    }, [perceivedProvisioningStartTime]);

    const {
        templateChoice = "unknown",
        templateChoiceId,
        templateMetadata
    } = useJiraTemplateChoice();

    useEffect(() => {
        const anchorLocalStorage = getAnchorLocalStorage();
        if (anchorLocalStorage) {
            try {
                const localStorageObj: JourneyLocalStorage =
                    JSON.parse(anchorLocalStorage);

                if (localStorageObj.persona) {
                    persona.current = localStorageObj.persona;
                }
            } catch (err) {
                console.log(err);
            }
        }

        if (templateMetadata?.persona) {
            persona.current = templateMetadata?.persona;
        } else if (selectedTeamType && !persona.current) {
            persona.current = `${getIdentityTeamType(
                selectedTeamType
            )} Generic`;
        }
    }, [templateMetadata, selectedTeamType]);

    const { getAnchorLocalStorage } = useAnchorLocalStorage();

    const localStorageObj: JourneyLocalStorage | undefined = useMemo(() => {
        const anchorLocalStorage = getAnchorLocalStorage();
        if (anchorLocalStorage) {
            try {
                return JSON.parse(anchorLocalStorage) as JourneyLocalStorage;
            } catch (err) {
                console.log(err);
                return undefined;
            }
        }

        return undefined;
    }, []);

    // send tailored-onboarding event before redirect to product.
    useEffect(() => {
        if (
            analyticsClient &&
            finalProvisioningScreens.includes(currentScreen) &&
            selectedTeamType &&
            persona.current &&
            cloudId &&
            userProfile.accountId
        ) {
            analyticsClient.setTenantInfo(tenantType.CLOUD_ID, cloudId);
            analyticsClient.setUserInfo(
                userType.ATLASSIAN_ACCOUNT,
                userProfile.accountId
            );
            createAnalyticsEvent({
                source: "wacProvisioningScreen",
                actionSubject: "tailored-onboarding",
                action: "initialized",
                actionSubjectId: "jira",
                attributes: {
                    anchor: localStorageObj?.anchor?.name,
                    type: localStorageObj?.anchor?.type,
                    team: getIdentityTeamType(selectedTeamType),
                    page_url: `${window.location.origin}/ondemand/signup/confirmation`,
                    page_path: "/ondemand/signup/confirmation",
                    persona: persona.current
                },
                eventType: "track"
            }).fire();
        }
    }, [currentScreen, selectedTeamType, analyticsClient, cloudId]);

    const createRedirectUrl = useCallback(() => {
        const url: URL = new URL(
            createBaseUrl(
                isProduction,
                onDemandUrl,
                products,
                dataRegion,
                cloudId,
                admin,
                createAnalyticsEvent
            )
        );

        // This causes browser to retry for 30 seconds if site isn't ready
        url.searchParams.set("src", "signup");

        const hasTemplateChoice = ["scrum", "kanban", "business"].includes(
            templateChoice
        );

        if (hasTemplateChoice) {
            url.searchParams.set("jsw_acp", "true");
            url.searchParams.set("template_choice", templateChoice);
        } else if (templateChoiceId) {
            //only set templateID if not scrum, kanban or business
            url.searchParams.set("templateId", templateChoiceId);
        }

        // Get persona data from state.
        if (persona.current) {
            url.searchParams.set("persona", persona.current);
        }

        if (hasTemplateChoice || templateChoiceId || persona.current) {
            /**
             * Signups that have a templateId set should not be redirected to /welcome/software
             *
             * If a templateId exists, this means it has been passed down in the signup context
             * and there will be a specific onboarding flow depending on the template.
             * */

            const landUsersOnJiraYourWork = FeatureGates.checkGate(
                "jfind-land-users-on-your-work"
            );

            if (!templateId) {
                url.pathname = url.pathname + "welcome/software";
            } else if (isProductJsw && landUsersOnJiraYourWork) {
                // temporary path until we can have templateId passed in query params instead of through signup context
                url.pathname = url.pathname + "jira/your-work";
            }
        }

        if (siteRenameRequestId) {
            url.searchParams.set("rename", "true");
        }
        if (signupSource === "bbbundle" && isFoundingAdmin === "true") {
            url.searchParams.set("signupSource", signupSource);
            url.searchParams.set("isFoundingAdmin", isFoundingAdmin);
        }

        return url.toString();
    }, [
        admin,
        cloudId,
        products,
        dataRegion,
        onDemandUrl,
        signupSource,
        isProduction,
        templateChoice,
        isFoundingAdmin,
        siteRenameRequestId,
        createAnalyticsEvent,
        selectedTeamType,
        templateChoiceId,
        persona
    ]);

    const [getCloudStatus, setCloudStatus] = useState<
        GetCloudStatusType | undefined
    >(undefined);

    const currentTime = Date.now();
    const [graphqlProvisioningMS, setGraphqlProvisioningMS] = useState(0);
    const [enrolledInGraphqlExperiment, setEnrolledInGraphqlExperiment] =
        useState<boolean>(orchestrationId ? true : false);
    const [graphqlProvisionComplete, setGraphqlProvisionComplete] =
        useState<boolean>(false);
    const [isInitialProvisionPolling, setIsInitialProvisionPolling] =
        useState<boolean>(true);

    const { data, loading, error } = useSubscription(
        PROVISIONING_STATUS_SUBSCRIPTION,
        {
            skip: !enrolledInGraphqlExperiment,
            variables: { orchestrationId },
            // https://www.apollographql.com/docs/react/data/subscriptions/#subscriptionhookoptions-interface-oncomplete
            onData: () => {
                // SLO "good event" for GraphQL subscription
                ufoEventGraphQLSubscriptionSetup.success({
                    customData: {
                        reactComponent: "CloudStatusProvider GraphQL",
                        analyticsEvent: "succeeded",
                        orchestrationId
                    }
                });
                ufoEventRedirectToProduct.endSubstep(GRAPHQL_SUBSCRIPTION_ACK);
            },
            // https://www.apollographql.com/docs/react/data/subscriptions/#subscriptionhookoptions-interface-onerror
            onError: (subscriptionError: unknown) => {
                // SLO "bad event" for GraphQL subscription
                ufoEventGraphQLSubscriptionSetup.failure({
                    error: subscriptionError,
                    customData: {
                        reactComponent: "CloudStatusProvider GraphQL",
                        analyticsEvent: "errored",
                        errorOrigin: "useSubscription.onError",
                        orchestrationId
                    }
                });
                setEnrolledInGraphqlExperiment(false);
            }
        }
    );

    // GraphQL experiment to listen to status change
    /* istanbul ignore next */
    useEffect(() => {
        if (!enrolledInGraphqlExperiment) {
            return;
        }
        try {
            // Guard to redirect users who bookmarked Confirmation page
            if (!!getCloudStatus && isInitialProvisionPolling) {
                const before = Date.now();
                setIsInitialProvisionPolling(false);
                getCloudStatus()
                    .then((ready) => {
                        if (ready) {
                            setGraphqlProvisionComplete(true);
                            onSuccess(before, Date.now(), Date.now() - before);
                        }
                    })
                    .catch((error) => {
                        setGraphqlProvisionComplete(true);
                        onError(before, Date.now(), Date.now() - before, error);
                    });
            }
            ufoEventGraphQLGetProvisioningStatus.start({ productKey });
            ufoEventRedirectToProduct.startSubstep(
                GRAPHQL_WEBHOOK_PROVISIONING_LATENCY
            );
            if (data?.signup?.signupProvisioningStatus) {
                const {
                    provisionComplete,
                    orchestrationId: orchestrationIdFromSubscription
                } = data.signup.signupProvisioningStatus;
                if (provisionComplete && orchestrationIdFromSubscription) {
                    trackGetProvisioningStatusLatencyEvent({
                        createAnalyticsEvent,
                        productKey,
                        provisioningScreenOnComplete: currentScreen,
                        fetchType: "graphql-webhook",
                        startTimeSeconds:
                            graphqlGetProvisioningStartTimeRef.current
                    });

                    // SLO "good event" for GraphQL get provisioning status
                    ufoEventGraphQLGetProvisioningStatus.success({
                        customData: {
                            reactComponent: "CloudStatusProvider GraphQL",
                            analyticsEvent: "succeeded",
                            // elapsedTimeMs: graphqlProvisioningMS
                            orchestrationId: orchestrationIdFromSubscription
                        }
                    });
                    setGraphqlProvisionComplete(true);

                    onSuccess(currentTime, Date.now(), graphqlProvisioningMS);
                }
            }
            if (error) {
                // SLO "bad event" for GraphQL subscription
                ufoEventGraphQLSubscriptionSetup.failure({
                    error,
                    customData: {
                        reactComponent: "CloudStatusProvider GraphQL",
                        analyticsEvent: "errored",
                        errorOrigin: "useSubscription, useEffect",
                        orchestrationId
                    }
                });
                throw error;
            }
        } catch (error) {
            // SLO "bad event" for GraphQL get provisioning status
            ufoEventGraphQLGetProvisioningStatus.failure({
                error,
                customData: {
                    reactComponent: "CloudStatusProvider GraphQL",
                    analyticsEvent: "errored",
                    elapsedTimeMs: graphqlProvisioningMS,
                    errorOrigin: "useEffect",
                    orchestrationId
                }
            });
            ufoEventRedirectToProduct.endSubstep(
                GRAPHQL_WEBHOOK_PROVISIONING_LATENCY,
                { customData: { orchestrationId } }
            );

            // onError(currentTime, Date.now(), graphqlProvisioningMS, error);
            // fallback to control status polling
            setEnrolledInGraphqlExperiment(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loading, error, getCloudStatus, enrolledInGraphqlExperiment]);

    // GraphQL experiment timeout listener
    useEffect(() => {
        if (!enrolledInGraphqlExperiment) {
            return;
        }
        if (graphqlProvisioningMS > GRAPHQL_EXPERIMENT_TIMEOUT) {
            // SLO "bad event" for GraphQL webhook provisioning status
            ufoEventGraphQLGetProvisioningStatus.failure({
                error: new Error("GraphQL timeout"),
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "timedOut",
                    orchestrationId
                }
            });

            // fallback to control status polling
            setEnrolledInGraphqlExperiment(false);
            // onTimeout(currentTime, Date.now(), graphqlProvisioningMS);
            return;
        }
        const intervalId = setInterval(() => {
            setGraphqlProvisioningMS(graphqlProvisioningMS + 1000);
        }, 1000);
        return () => clearInterval(intervalId);
    }, [enrolledInGraphqlExperiment, platform, graphqlProvisioningMS]);

    const fireUfoEventForPollingResult = (
        pollingResult: "success" | "error" | "timeout",
        elapsedTime: number,
        error?: unknown
    ): void => {
        try {
            const { timingKey, requestPath } =
                getUfoSubstepForPolling(platform);

            const errorOrigin = error
                ? `CloudStatusProvider polling ${pollingResult}`
                : undefined;

            // Performance metrics for polling duration
            ufoEventRedirectToProduct.endSubstep(timingKey, {
                ...(error ? { error } : {}),
                customData: {
                    reqPath: requestPath,
                    elapsedTimeMs: elapsedTime,
                    ...(errorOrigin ? { errorOrigin } : {})
                }
            });

            if (pollingResult === "success") {
                // SLO "good event" for cloud status polling
                ufoEventConfirmationPageStatusPolling.success({
                    customData: {
                        reactComponent: "CloudStatusProvider",
                        analyticsEvent: "succeeded",
                        reqPath: requestPath,
                        elapsedTimeMs: elapsedTime
                    }
                });
            } else if (pollingResult === "error") {
                // SLO "bad event" for cloud status polling
                ufoEventConfirmationPageStatusPolling.failure({
                    error,
                    customData: {
                        reactComponent: "CloudStatusProvider",
                        analyticsEvent: "errored",
                        reqPath: requestPath,
                        elapsedTimeMs: elapsedTime,
                        errorOrigin
                    }
                });
            } else if (pollingResult === "timeout") {
                // SLO "bad event" for provisioning status
                ufoEventConfirmationPageStatusPolling.failure({
                    error,
                    customData: {
                        reactComponent: "CloudStatusProvider",
                        analyticsEvent: "timedOut",
                        elapsedTimeMs: elapsedTime,
                        errorOrigin
                    }
                });
            }

            // GasV3 event to measure polling provisioning latency
            trackGetProvisioningStatusLatencyEvent({
                createAnalyticsEvent,
                productKey,
                provisioningScreenOnComplete: currentScreen,
                fetchType: "polling",
                elapsedTimeSeconds: Math.round(elapsedTime / 1000)
            });
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (platform === "ccp") {
            setCloudStatus(() => async () => {
                if (!provisioningRequestId) {
                    return Promise.resolve(false);
                }
                const requestIdsResp = async (product: string) =>
                    await stargateClient.getProductActivationStatus({
                        provisioningRequestId: product
                    });

                const requestIds = await Promise.all(
                    provisioningRequestId.split(",").map(async (product) => {
                        try {
                            console.info(
                                `Requesting Ids ${JSON.stringify(product)}`
                            );
                            return await requestIdsResp(product);
                        } catch (error) {
                            //@ts-ignore
                            if (error.response?.status === 400) {
                                ufoEventRedirectToProduct.abort({
                                    customData: {
                                        reactComponent: "CloudStatusProvider",
                                        analyticsEvent: "redirect",
                                        redirectFrom: "confirmationPage",
                                        errorOrigin: "StaleProvisioningRedirect"
                                    },
                                    error
                                });
                                window.location.assign(createRedirectUrl());
                            }

                            // retry if the first call fails
                            console.info(
                                `Retry Requesting Ids ${JSON.stringify(
                                    product
                                )}`
                            );
                            return await requestIdsResp(product);
                        }
                    })
                );

                // check for cloudId in the status response
                if (!cloudId) {
                    const detectedCloudId =
                        getCloudIdFromProductActivationStatuses(requestIds);
                    if (detectedCloudId) {
                        setCloudId(detectedCloudId);
                    }
                }

                for (const resource of requestIds) {
                    if (
                        resource.resourcePackages.find(
                            (item) => item.action.status === "IN_PROGRESS"
                        )
                    )
                        return Promise.resolve(false);

                    if (
                        resource.resourcePackages.find(
                            (item) =>
                                item.action.status === "PROVISIONING_FAILURE"
                        )
                    ) {
                        ufoEventRedirectToProduct.setCustomData({
                            activationStatus: "PROVISIONING_FAILURE",
                            provisioningRequestId
                        });
                        return Promise.reject(
                            new Error(
                                `Provisioning Failure CCP with cloudId ${cloudId}: ${JSON.stringify(
                                    resource.resourcePackages[0]?.action
                                        .errorDetails
                                )}`
                            )
                        );
                    }
                }

                return Promise.resolve(true);
            });
        } else {
            setCloudStatus(() => async () => {
                if (!cloudId || !requestId) {
                    return Promise.resolve(false);
                }

                const cloudStatusResp = async () => {
                    const { completed, successful } =
                        await cofsClient.getCloudStatus({
                            cloudId,
                            requestId
                        });
                    return Promise.resolve(completed && successful);
                };

                try {
                    return await cloudStatusResp();
                } catch {
                    // retry if the first call fails
                    return await cloudStatusResp();
                }
            });
        }
    }, [platform, requestId]);

    useEffect(() => {
        if (enrolledInGraphqlExperiment) {
            return;
        }
        const { timingKey, requestPath } = getUfoSubstepForPolling(platform);

        // Add product cohort to UFO event (ie. metrics by product)
        // Start UFO to measure the total duration and reliability of provisioning status polling
        ufoEventConfirmationPageStatusPolling.start({ productKey });
        ufoEventRedirectToProduct.startSubstep(timingKey);

        if (!hasRequiredParameters) {
            if (createCloudStatus !== "error") {
                // SLO "bad event" for provisioning status polling
                ufoEventConfirmationPageStatusPolling.failure({
                    error: new Error("missingRequiredParameters"),
                    customData: {
                        reactComponent: "CloudStatusProvider",
                        analyticsEvent: "missingRequiredParameters",
                        provisioningStatus: createCloudStatus,
                        errorOrigin: "hasRequiredParameters"
                    }
                });

                // Performance metrics for polling duration
                ufoEventRedirectToProduct.endSubstep(timingKey, {
                    error: new Error("missingRequiredParameters"),
                    customData: {
                        provisioningStatus: createCloudStatus,
                        reqPath: requestPath,
                        errorOrigin: "hasRequiredParameters"
                    }
                });

                createAnalyticsEvent({
                    event: "errored",
                    eventName: "missingRequiredParameters"
                }).fire("signup");
                setCreateCloudStatus("error");
            }
            return;
        }

        getCloudStatus &&
            pollCloudStatusWithFeatureFlag(
                {
                    getCloudStatus,
                    startTime: Date.now(),
                    retryInterval,
                    timeout,
                    onSuccess,
                    onError,
                    onTimeout,
                    fireUfoEventForPollingResult
                },
                is15secondProvisioningEnabled
            );
    }, [hasRequiredParameters, getCloudStatus, enrolledInGraphqlExperiment]);

    // Handles the progress bar update
    // https://hello.atlassian.net/wiki/spaces/BXP/pages/2602964460/USC-898+Fixing+the+dilemma+of+the+progress+bar+by+implementing+a+piecewise+function
    useEffect(() => {
        const startTime = Date.now();
        const pivotPercentage = 0.8; // The progress bar will fill up to 80% by the pivot time
        const progressBarPivotPoint = is15secondProvisioningEnabled
            ? 30 * 1000
            : 60 * 1000; // the pivot point after which the progress bar will slow down
        const slowProgressIncrement =
            (1 - pivotPercentage) /
            ((DEFAULT_TIMEOUT - progressBarPivotPoint) / 100);
        const slowProgress = 0;

        const interval = setInterval(() => {
            setProgress(
                getProgressbarStatus({
                    startTime,
                    pivotPercentage,
                    progressBarPivotPoint,
                    slowProgressIncrement,
                    slowProgress,
                    interval
                })
            );
        }, 100); // Update progress every 0.1 second

        return () => {
            setProgress(0);
            clearInterval(interval);
        };
    }, [is15secondProvisioningEnabled]);

    // On provisioning finish UFO, analytics and redirection handling
    useEffect(() => {
        if (enrolledInGraphqlExperiment && !graphqlProvisionComplete) {
            return;
        }
        if (createCloudStatus !== "success") {
            return;
        }

        const fireProvisionSuccessUFOAnalyticsEvent = () => {
            createAnalyticsEvent({
                eventName: "redirectedToProduct",
                eventComponent: "confirmationPage",
                event: "redirected",
                eventType: "ui",
                action: "redirected"
            }).fire();

            trackPerceivedProvisioningEvent({
                createAnalyticsEvent,
                productKey,
                finalProvisioningScreen: currentScreen,
                startTimeSeconds: perceivedProvisioningStartTimeRef.current
            });

            // SLO "good event" for redirect into product
            if (graphqlProvisionComplete) {
                ufoEventRedirectToProduct.success({
                    customData: {
                        reactComponent: "CloudStatusProvider GraphQL",
                        analyticsEvent: "redirected",
                        redirectFrom: "GraphQLWebhook",
                        reqPath: "graphql-subscription",
                        orchestrationId
                    }
                });
            } else {
                ufoEventRedirectToProduct.success({
                    customData: {
                        reactComponent: `CloudStatusProvider ${currentScreen}`,
                        analyticsEvent: "redirected",
                        redirectFrom: "confirmationPage",
                        provisioningStatus: bundleProvisioningStatus,
                        finalProvisioningScreen: currentScreen
                    }
                });
            }
        };

        if (
            // verify user has made it to a final screen (i.e. robots, jira-animated-provisioning-screen, redirect).
            finalProvisioningScreens.includes(currentScreen) &&
            (isFreeBundlesEnabled || isPageOverlayModalEnabled)
        ) {
            switch (bundleProvisioningStatus) {
                case "payload-ready":
                case "no-payload":
                    // initial site is provisioned, now let Bundles UI know
                    // it can provision secondary products
                    setBundleProvisioningStatus("provisioning");
                    return;
                case "success":
                    if (deferRedirectView) {
                        setRedirectUrl(createRedirectUrl());
                        return;
                    }
                    fireProvisionSuccessUFOAnalyticsEvent();
                    // Bundles UI has completed provisioning secondary products
                    window.location.assign(createRedirectUrl());
                    return;
                case "waiting":
                    // Bundles modal should alert the user that product is ready
                    if (isPageOverlayModalEnabled) {
                        setBundleProvisioningStatus("provisioning");
                    }
                    return;
                default:
                    return;
            }
        }

        // verify user has made it to a final screen (i.e. robots, jira-animated-provisioning-screen, redirect).
        if (finalProvisioningScreens.includes(currentScreen)) {
            if (deferRedirectView) {
                setRedirectUrl(createRedirectUrl());
                return;
            }

            createAnalyticsEvent({
                eventName: "redirectedToProduct",
                eventComponent: "confirmationPage",
                event: "redirected",
                eventType: "ui",
                action: "redirected"
            }).fire();

            trackPerceivedProvisioningEvent({
                createAnalyticsEvent,
                productKey,
                finalProvisioningScreen: currentScreen,
                startTimeSeconds: perceivedProvisioningStartTimeRef.current
            });

            // SLO "good event" for redirect into product
            ufoEventRedirectToProduct.success({
                customData: {
                    reactComponent: "CloudStatusProvider",
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    finalProvisioningScreen: currentScreen
                }
            });

            window.location.assign(createRedirectUrl());
        }
    }, [
        currentScreen,
        createCloudStatus,
        bundleProvisioningStatus,
        graphqlProvisionComplete
    ]);

    const onSuccess = (
        startTime: number,
        endTime: number,
        elapsedTime: number
    ) => {
        if (is15secondProvisioningEnabled) {
            createAnalyticsEvent({
                eventType: "operational",
                action: "succeeded",
                actionSubject:
                    "provisioning-loading-screen-with-improved-polling",
                source: "bxpkit-wac-confirmation-page",
                attributes: {
                    totalElapsedTime: endTime - startTime,
                    timeDiffFromPrev5secondPoll: (endTime - startTime) % 5000,
                    timeDiffFromNext5secondPoll:
                        5000 - ((endTime - startTime) % 5000)
                }
            }).fire("signup");
        }

        createAnalyticsEvent({
            eventType: "operational",
            eventName: "createCloudSucceeded",
            startTime,
            endTime,
            elapsedTime
        }).fire("signup");
        setProgress(1); // Progress bar to 100% before redirecting user to product
        setCreateCloudStatus("success");
        if (
            !shouldShowUserSegmentationPage &&
            featureFlags.provisionSecondPartyApps &&
            provisionApp &&
            platform !== "ccp" &&
            !!appKey &&
            !!onDemandUrl
        ) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            const bxpExpressClient = useBXPExpressClient();
            void bxpExpressClient.getXsrfToken().then((xsrfToken) => {
                bxpExpressClient
                    .marketplaceAppInstall({
                        siteName: onDemandUrl,
                        appKey,
                        xsrfToken
                    })
                    .catch((err) => err);
            });
        }
    };

    const onError = (
        startTime: number,
        endTime: number,
        elapsedTime: number,
        error: unknown
    ) => {
        createAnalyticsEvent({
            event: "errored",
            eventName: "createCloudErrored",
            startTime,
            endTime,
            elapsedTime,
            error
        }).fire("signup");

        setCreateCloudStatus("error");
    };

    const onTimeout = (
        startTime: number,
        endTime: number,
        elapsedTime: number
    ) => {
        createAnalyticsEvent({
            event: "timedOut",
            eventName: "createCloudTimedOut",
            startTime,
            endTime,
            elapsedTime
        }).fire("signup");
        setCreateCloudStatus("timeout");
    };

    return (
        <CloudStatusContext.Provider
            value={{
                isReferredFromVerifyEmail,
                createCloudStatus,
                progress,
                featureFlags,
                deferRedirectView,
                setDeferRedirectView,
                redirectUrl,
                perceivedProvisioningStartTime,
                setPerceivedProvisioningStartTime
            }}
        >
            {children}
        </CloudStatusContext.Provider>
    );
};

export type PollCloudStatusOptions = {
    readonly getCloudStatus: GetCloudStatusType;
    readonly startTime: number;
    readonly retryInterval: number;
    readonly timeout: number;

    readonly onSuccess: (
        startTime: number,
        endTime: number,
        elapsedTime: number
    ) => void;
    readonly onError: (
        startTime: number,
        endTime: number,
        elapsedTime: number,
        error: unknown
    ) => void;
    readonly onTimeout: (
        startTime: number,
        endTime: number,
        elapsedTime: number
    ) => void;
    // readonly onResponse: (elapsedTime: number) => void;
    readonly fireUfoEventForPollingResult: (
        pollingResult: "success" | "error" | "timeout",
        elapsedTime: number,
        error?: unknown
    ) => void;
};

export const getPrimaryProduct = (products: readonly string[]) => {
    if (products.length > 0 && products[0] !== "") {
        return products[0];
    } else {
        return undefined;
    }
};

const getTopLevelDomain = (isProduction: boolean): string => {
    if (isProduction) {
        return ".atlassian.net";
    } else {
        return ".jira-dev.com";
    }
};

const isValidOnDemandUrl = (siteName: string) => {
    const isUrl = siteName.includes(".");
    return !isUrl;
};

const createBaseUrl = (
    isProduction: boolean,
    onDemandUrl: string | undefined,
    products: readonly string[],
    dataRegion: string | undefined,
    cloudId: string,
    admin: boolean | undefined,
    createAnalyticsEvent: CreateUIAnalyticsEvent
): string => {
    if (!isValidOnDemandUrl(onDemandUrl || "")) {
        createAnalyticsEvent({
            actionSubject: "invalidUrlOnRedirect",
            eventComponent: "confirmationPage",
            event: "event",
            eventType: "ui"
        }).fire("signup");
        window.history.back();
    }
    const instanceUrl =
        "https://" +
        encodeURIComponent(String(onDemandUrl)) +
        getTopLevelDomain(isProduction);

    const primaryProduct: string | undefined = getPrimaryProduct(products);

    if (admin) {
        return getAdminhubRedirectUrl(isProduction, cloudId);
    }

    if (typeof primaryProduct !== "string") {
        return instanceUrl;
    }

    if (primaryProduct === "opsgenie") {
        return getOpsgenieRedirectUrl(isProduction, onDemandUrl, dataRegion);
    }

    if (primaryProduct === "statuspage") {
        return getStatuspageRedirectUrl(isProduction, cloudId);
    }

    if (primaryProduct === "com.atlassian.bitbucket") {
        return getBitbucketRedirectUrl(isProduction);
    }

    if (primaryProduct === "confluence.ondemand") {
        return getConfluenceJiraRedirectUrl(instanceUrl);
    }
    return instanceUrl;
};

const getConfluenceJiraRedirectUrl = function (instanceUrl: string) {
    return instanceUrl + "/wiki";
};

const getBitbucketRedirectUrl = function (isProduction: boolean) {
    if (!isProduction) {
        return "https://integration.bb-inf.net/dashboard/overview";
    }

    return "https://bitbucket.org/dashboard/overview";
};

const getOpsgenieRedirectUrl = function (
    isProduction: boolean,
    onDemandUrl: string | undefined,
    dataRegion: string | undefined
) {
    if (!isProduction) {
        return `https://${onDemandUrl}.atlassian-app.opsgeni.us`;
    }
    if (dataRegion === "europe") {
        return `https://${onDemandUrl}.atlassian-app.eu.opsgenie.com`;
    }
    return `https://${onDemandUrl}.atlassian-app.opsgenie.com`;
};

const getStatuspageRedirectUrl = function (
    isProduction: boolean,
    cloudId: string
) {
    if (!isProduction) {
        return "https://manage.statuspagestaging.com/cloud/" + cloudId;
    }

    return "https://manage.statuspage.io/cloud/" + cloudId;
};

const getAdminhubRedirectUrl = (isProduction: boolean, cloudId: string) => {
    if (!isProduction) {
        return "https://admin.stg.atlassian.com/s/" + cloudId + "/setup";
    }

    return "https://admin.atlassian.com/s/" + cloudId + "/setup";
};

const getCloudIdFromProductActivationStatuses = (
    ccpResponses: ProductActivationStatusResponse[]
): string | undefined =>
    ccpResponses
        .map(({ resourcePackages }) =>
            resourcePackages
                .map(
                    ({ resourceAris }) =>
                        resourceAris
                            .find((resourceAri) =>
                                resourceAri.startsWith(
                                    "ari:cloud:platform::site/"
                                )
                            )
                            ?.split("/")[1]
                )
                .find((e) => e)
        )
        .find((e) => e);

function pollCloudStatusWithFeatureFlag(
    options: PollCloudStatusOptions,
    is15secondProvisioningEnabled: boolean
): void {
    if (is15secondProvisioningEnabled) {
        return pollCloudStatusFor15secondProvisioning(options);
    }
    return pollCloudStatus(options);
}

function pollCloudStatus(options: PollCloudStatusOptions): void {
    const {
        getCloudStatus,
        startTime,
        retryInterval,
        timeout,
        onSuccess,
        onError,
        onTimeout,
        fireUfoEventForPollingResult
    } = options;
    const endTime = Date.now();
    const elapsedTime = endTime - startTime;
    // onResponse(elapsedTime);
    if (elapsedTime < timeout) {
        getCloudStatus()
            .then((ready) => {
                if (ready) {
                    fireUfoEventForPollingResult("success", elapsedTime);
                    onSuccess(startTime, endTime, elapsedTime);
                } else {
                    setTimeout(() => pollCloudStatus(options), retryInterval);
                }
            })
            .catch((error) => {
                fireUfoEventForPollingResult("error", elapsedTime, error);
                onError(startTime, endTime, elapsedTime, error);
            });
    } else {
        fireUfoEventForPollingResult(
            "timeout",
            elapsedTime,
            new Error("pollCloudStatus timeout")
        );
        onTimeout(startTime, endTime, elapsedTime);
    }
}

/**
 * As a part of https://team.atlassian.com/project/ATLAS-37332, Jira site provisioning has been reduced to about 15
 * seconds. This polling function has been optimised to poll faster during the expected provisioning completion range to
 * land customers in the site faster.
 */
export function pollCloudStatusFor15secondProvisioning(
    options: PollCloudStatusOptions,
    run = 0
): void {
    const {
        getCloudStatus,
        startTime,
        timeout,
        onSuccess,
        onError,
        onTimeout,
        fireUfoEventForPollingResult
    } = options;

    function poll() {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;

        if (elapsedTime > timeout) {
            fireUfoEventForPollingResult(
                "timeout",
                elapsedTime,
                new Error("pollCloudStatusFor15secondProvisioning timeout")
            );
            onTimeout(startTime, endTime, elapsedTime);
            return;
        }

        getCloudStatus()
            .then((ready) => {
                if (ready) {
                    fireUfoEventForPollingResult("success", elapsedTime);
                    onSuccess(startTime, endTime, elapsedTime);
                } else {
                    pollCloudStatusFor15secondProvisioning(options, run + 1);
                }
            })
            .catch((error) => {
                fireUfoEventForPollingResult("error", elapsedTime, error);
                onError(startTime, endTime, elapsedTime, error);
            });
    }

    // initial run, check immediately to avoid rate limiting issues
    if (run === 0) {
        poll();
        return;
    }
    // we expect the majority of requests to be completed in the 15-30 second range, wait until 15 seconds to begin fast polling
    if (run === 1) {
        setTimeout(() => poll(), 15000);
        return;
    }
    // in the 15-30 seconds range, fast poll every 2 seconds
    if (run > 1 && run < 10) {
        setTimeout(() => poll(), 2000);
        return;
    }
    // if provisioning is delayed, slow poll every 10 seconds until the timeout if necessary
    setTimeout(() => poll(), 10000);
}

/**
 * useConfirmationPageOverlayModal
 *
 * Hook that determines whether or not to render the bundle or video modal overlay UI.
 * Checks for enable-confirmation-page-overlay feature flag,
 * ensures that enable-confirmation-page-overlay applies only to
 * single-product signups for Confluence.
 *
 * @returns boolean
 */
export function useConfirmationPageOverlayModal(
    featureFlags: FeatureFlags
): boolean {
    const { products, cloudId } = useSearchParameters();
    return (
        !!featureFlags?.enableConfirmationPageOverlay &&
        (featureFlags.enableConfirmationPageOverlay ===
            ConfirmationPageOverlayVariants.BUNDLES_MODAL ||
            featureFlags.enableConfirmationPageOverlay ===
                ConfirmationPageOverlayVariants.VIDEO_OVERLAY) &&
        products.length === 1 &&
        cloudId !== "" &&
        products.includes("confluence.ondemand")
    );
}
